import React, { useState, useCallback } from 'react'
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Autocomplete,
  Alert,
  CircularProgress,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material'
import { Delete as DeleteIcon, Add as AddIcon, ArrowBack as BackIcon } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useCreatePurchaseInvoice } from '../../hooks/api/usePurchaseInvoices'
import { useSKUs } from '../../hooks/api/useSKUs'
import { useVendors } from '../../hooks/api/useVendors'
import { SKU, Vendor } from '../../types'
import PaymentAccountPicker from '../../components/ui/PaymentAccountPicker'
import { toPaisa, fromPaisa } from '../../utils/money'

interface LineItem {
  sku_id: string | null
  cartons_packing: number
  quantity_ctn: number
  quantity_pcs: number
  rate: number
  line_total: number
}

const emptyLine = (): LineItem => ({
  sku_id: null,
  cartons_packing: 0,
  quantity_ctn: 0,
  quantity_pcs: 0,
  rate: 0,
  line_total: 0,
})

const recalcLine = (item: LineItem): LineItem => ({
  ...item,
  line_total: fromPaisa(toPaisa(item.quantity_ctn * item.rate)),
})

const pkr = (n: number) =>
  n.toLocaleString('en-PK', { style: 'currency', currency: 'PKR', maximumFractionDigits: 2 })

const PurchaseInvoiceNewPage: React.FC = () => {
  const navigate = useNavigate()
  const createMutation = useCreatePurchaseInvoice()

  const { data: skusData } = useSKUs({ limit: 500 })
  const skus: SKU[] = skusData?.results ?? []

  const { data: vendors = [] } = useVendors({ active_only: true })
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null)

  const [invoiceDate, setInvoiceDate] = useState(() => new Date().toISOString().split('T')[0])

  const [lineItems, setLineItems] = useState<LineItem[]>([emptyLine()])
  const [discount, setDiscount] = useState(0)
  const [isPaid, setIsPaid] = useState(false)
  const [paymentSource, setPaymentSource] = useState('cash')
  const [validationError, setValidationError] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)

  const applySkuDefaults = useCallback((index: number, sku: SKU | null) => {
    setLineItems((prev) => {
      const updated = [...prev]
      const existing = updated[index]
      const patched: LineItem = {
        ...existing,
        sku_id: sku?._id ?? null,
        cartons_packing: sku?.packing ?? 0,
        rate: sku?.purchase_rate ?? 0,
        quantity_pcs: existing.quantity_ctn * (sku?.packing ?? 0),
      }
      updated[index] = recalcLine(patched)
      return updated
    })
  }, [])

  const updateCtn = useCallback((index: number, qty_ctn: number) => {
    setLineItems((prev) => {
      const updated = [...prev]
      const item = updated[index]
      updated[index] = recalcLine({
        ...item,
        quantity_ctn: qty_ctn,
        quantity_pcs: qty_ctn * item.cartons_packing,
      })
      return updated
    })
  }, [])

  const addLine = () => setLineItems((prev) => [...prev, emptyLine()])
  const removeLine = (index: number) =>
    setLineItems((prev) => prev.filter((_, i) => i !== index))

  const subtotal = fromPaisa(lineItems.reduce((acc, item) => acc + toPaisa(item.line_total), 0))
  const grandTotal = fromPaisa(toPaisa(subtotal) - toPaisa(discount))

  const handleSaveClick = () => {
    setValidationError('')
    if (!selectedVendor) return setValidationError('Please select a vendor.')
    if (!invoiceDate) return setValidationError('Invoice date is required.')
    if (lineItems.some((item) => !item.sku_id))
      return setValidationError('Select a SKU for every line item.')
    setConfirmOpen(true)
  }

  const handleConfirmSave = async () => {
    setConfirmOpen(false)
    try {
      const invoice = await createMutation.mutateAsync({
        vendor_id: selectedVendor!._id,
        invoice_date: invoiceDate,
        is_paid: isPaid,
        ...(isPaid && {
          payment_account: paymentSource === 'cash' ? 'cash' as const : 'bank' as const,
          ...(paymentSource !== 'cash' && { bank_account_id: paymentSource }),
        }),
        line_items: lineItems.map((item) => ({
          sku_id: item.sku_id!,
          quantity_ctn: item.quantity_ctn,
          quantity_pcs: item.quantity_pcs,
          rate: item.rate,
          net_value_before_tax: item.line_total,
        })),
        summary: {
          net_value_before_tax: grandTotal,
        },
      })
      navigate(`/purchase-invoices/${invoice._id}`)
    } catch {
      // handled by mutation onError
    }
  }

  return (
    <Box sx={{ maxWidth: '100%', overflow: 'hidden' }}>
      {/* ── Top toolbar ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1.5,
          pt: 2,
          mb: 2,
        }}
      >
        {/* Left: back + title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate('/purchase-invoices')} size="small">
            <BackIcon />
          </IconButton>
          <Typography variant="h6" fontWeight={600}>
            New Purchase Invoice
          </Typography>
        </Box>

        {/* Right: payment toggle + action buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            value={isPaid ? 'paid' : 'unpaid'}
            exclusive
            onChange={(_, val) => { if (val !== null) setIsPaid(val === 'paid') }}
            size="small"
          >
            <ToggleButton value="unpaid" color="warning">Unpaid</ToggleButton>
            <ToggleButton value="paid" color="success">Paid</ToggleButton>
          </ToggleButtonGroup>
          {isPaid && <Box sx={{ minWidth: 190 }}><PaymentAccountPicker value={paymentSource} onChange={setPaymentSource} label="Paid from" /></Box>}

          <Button
            variant="contained"
            onClick={handleSaveClick}
            disabled={createMutation.isPending}
            startIcon={createMutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {createMutation.isPending ? 'Saving...' : 'Save Invoice'}
          </Button>
          <Button variant="outlined" onClick={() => navigate('/purchase-invoices')}>
            Cancel
          </Button>
        </Box>
      </Box>

      {validationError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setValidationError('')}>
          {validationError}
        </Alert>
      )}

      {/* ── Vendor & Invoice Details ── */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Vendor & Invoice Details
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Autocomplete
              size="small"
              options={vendors}
              getOptionLabel={(v) => v.name}
              value={selectedVendor}
              onChange={(_, val) => setSelectedVendor(val)}
              renderInput={(params) => (
                <TextField {...params} label="Vendor *" placeholder="Select vendor..." required />
              )}
              noOptionsText={
                <Box>
                  No vendors found.{' '}
                  <Button size="small" onClick={() => window.open('/settings/vendors', '_blank')}>
                    Add in Settings
                  </Button>
                </Box>
              }
            />
          </Grid>

          {selectedVendor && (
            <Grid item xs={12} sm={6} md={8}>
              <Box
                sx={{
                  px: 2, py: 1,
                  bgcolor: '#faf9f7',
                  border: '1px solid',
                  borderColor: '#e8e4dd',
                  borderRadius: 1,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  alignItems: 'center',
                }}
              >
                {selectedVendor.phone && (
                  <Typography variant="caption"><strong>Phone:</strong> {selectedVendor.phone}</Typography>
                )}
                {selectedVendor.ntn && (
                  <Typography variant="caption"><strong>NTN:</strong> {selectedVendor.ntn}</Typography>
                )}
                {selectedVendor.strn && (
                  <Typography variant="caption"><strong>STRN:</strong> {selectedVendor.strn}</Typography>
                )}
                {selectedVendor.address && (
                  <Typography variant="caption"><strong>Address:</strong> {selectedVendor.address}</Typography>
                )}
              </Box>
            </Grid>
          )}

          <Grid item xs={12} sm={4} md={3}>
            <TextField fullWidth required size="small" label="Invoice Date *" type="date"
              value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)}
              InputLabelProps={{ shrink: true }} />
          </Grid>
        </Grid>
      </Paper>

      {/* ── Line Items ── */}
      <Paper sx={{ p: { xs: 1.5, sm: 3 }, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Line Items
        </Typography>

        <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <Table
            size="small"
            sx={{
              minWidth: 500,
              '& th': { fontWeight: 600, whiteSpace: 'nowrap', bgcolor: '#f7f5f2', fontSize: 12 },
              '& td': { verticalAlign: 'middle', py: 1 },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 200 }}>SKU</TableCell>
                <TableCell align="right" sx={{ minWidth: 90 }}>Qty CTN</TableCell>
                <TableCell align="right" sx={{ minWidth: 70 }}>PCS</TableCell>
                <TableCell align="right" sx={{ minWidth: 90 }}>Rate / CTN</TableCell>
                <TableCell align="right" sx={{ minWidth: 100 }}>Total</TableCell>
                <TableCell sx={{ width: 36 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {lineItems.map((item, idx) => (
                <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#eeebe5' } }}>
                  {/* SKU */}
                  <TableCell>
                    <Autocomplete
                      size="small"
                      options={skus}
                      getOptionLabel={(o) => o.name}
                      value={skus.find((s) => s._id === item.sku_id) ?? null}
                      onChange={(_, val) => applySkuDefaults(idx, val)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select SKU"
                          size="small"
                          InputProps={{ ...params.InputProps, style: { fontSize: 13 } }}
                        />
                      )}
                      noOptionsText="No SKUs found"
                      ListboxProps={{ style: { fontSize: 13 } }}
                    />
                  </TableCell>

                  {/* Qty CTN */}
                  <TableCell align="right">
                    <TextField
                      type="number"
                      value={item.quantity_ctn || ''}
                      onChange={(e) => updateCtn(idx, parseFloat(e.target.value) || 0)}
                      size="small"
                      inputProps={{ min: 0, step: 1, style: { textAlign: 'right', padding: '5px 8px' } }}
                      sx={{ width: 80 }}
                    />
                  </TableCell>

                  {/* PCS — read-only */}
                  <TableCell align="right">
                    <Typography variant="body2" color="text.secondary">
                      {item.quantity_pcs || '—'}
                    </Typography>
                  </TableCell>

                  {/* Rate — read-only, auto from SKU */}
                  <TableCell align="right">
                    <Typography variant="body2">
                      {item.rate ? item.rate.toLocaleString() : '—'}
                    </Typography>
                  </TableCell>

                  {/* Total */}
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={700} color="primary.main">
                      {item.line_total ? item.line_total.toLocaleString() : '—'}
                    </Typography>
                  </TableCell>

                  {/* Remove */}
                  <TableCell>
                    <Tooltip title="Remove row">
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeLine(idx)}
                          disabled={lineItems.length === 1}
                        >
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        <Button startIcon={<AddIcon />} onClick={addLine} sx={{ mt: 1.5 }} variant="outlined" size="small">
          Add Row
        </Button>
      </Paper>

      {/* ── Summary ── */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box sx={{ maxWidth: 340, ml: 'auto' }}>
          {/* Subtotal */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography variant="body2" color="text.secondary">Total</Typography>
            <Typography variant="body2" fontWeight={600}>{pkr(subtotal)}</Typography>
          </Box>

          {/* Discount */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="body2" color="text.secondary">Discount</Typography>
            <TextField
              type="number"
              value={discount || ''}
              onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
              size="small"
              inputProps={{ min: 0, step: 'any', style: { textAlign: 'right', padding: '4px 8px' } }}
              InputProps={{ startAdornment: <InputAdornment position="start">Rs</InputAdornment> }}
              sx={{ width: 160 }}
            />
          </Box>

          <Box sx={{ borderTop: '2px solid', borderColor: 'primary.main', pt: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1" fontWeight={700}>Grand Total</Typography>
              <Typography variant="h6" fontWeight={700} color="primary.main">
                {pkr(grandTotal)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* ── Confirm Save Dialog ── */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Confirm Purchase Invoice</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            You are about to save the following invoice. Stock levels will be updated immediately.
          </Typography>

          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Vendor</Typography>
              <Typography variant="body2" fontWeight={600}>{selectedVendor?.name}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Invoice Date</Typography>
              <Typography variant="body2" fontWeight={600}>{invoiceDate}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Line Items</Typography>
              <Typography variant="body2" fontWeight={600}>{lineItems.length}</Typography>
            </Box>
            <Divider />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Grand Total</Typography>
              <Typography variant="body1" fontWeight={700} color="primary.main">{pkr(grandTotal)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Payment Status</Typography>
              <Typography
                variant="body2"
                fontWeight={600}
                color={isPaid ? 'success.main' : 'warning.main'}
              >
                {isPaid ? 'Paid' : 'Unpaid'}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} variant="outlined">
            Go Back
          </Button>
          <Button onClick={handleConfirmSave} variant="contained" color="primary">
            Confirm & Save
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  )
}

export default PurchaseInvoiceNewPage
